import React from 'react';
import styled from 'styled-components';
import { Rnd } from 'react-rnd';
import { token } from '@bynder/design-system';
import { SpacingWidgetOptions } from '../../constants/widgets';
import { SPACER_LOCAL_STORAGE, getDefaultSpacerHeight } from './helpers';
import { removeDot, useWidgetStyleProperty } from '../guides/Inspector/helpers';
import { CSS_CLASSES } from '../guides/Inspector/constants';

interface Props {
  height?: number;
  readOnly?: boolean;
  divider: SpacingWidgetOptions;
  onResize: (newHeight: number) => void;
  stylesheet: string;
  $borderColor?: string;
}

const SpacerWidget: React.FunctionComponent<Props> = ({
  height,
  readOnly,
  divider,
  onResize,
  stylesheet,
  $borderColor,
}) => {
  const [stateHeight, setStateHeight] = React.useState(
    height ? `${height}px` : `${getDefaultSpacerHeight()}px`,
  );

  React.useEffect(() => {
    if (height) {
      setStateHeight(`${height}px`);
    }
  }, [height]);

  const updateSpacer = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _e: any,
    _direction: string,
    ref: React.ElementRef<'div'>,
  ) => {
    const newHeight = parseInt(ref.style.height.slice(0, -2), 10);
    onResize(newHeight);
    window.localStorage.setItem(SPACER_LOCAL_STORAGE, newHeight.toString());
  };

  const handleResize = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _e: any,
    _direction: string,
    ref: React.ElementRef<'div'>,
  ) => {
    setStateHeight(ref.style.height);
  };

  const isNone = divider === SpacingWidgetOptions.None || !divider;
  const isDashed = divider === SpacingWidgetOptions.Dashed;
  const isSolid = divider === SpacingWidgetOptions.Solid;

  const { value: spacingBorderColor } = useWidgetStyleProperty(
    stylesheet,
    'widgets.spacingDivider.color.hexCode',
    CSS_CLASSES.DIVIDER_LINE,
  );

  const $spacingDividerColor = $borderColor ?? spacingBorderColor;

  return !readOnly ? (
    <StyledSpacerWidget
      data-testid="spacerWidget"
      disableDragging
      minHeight={24}
      maxHeight={500}
      size={{ width: '100%', height: stateHeight }}
      onResize={handleResize}
      onResizeStop={updateSpacer}
      enableResizing={{
        top: false,
        right: false,
        bottom: true,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      }}
    >
      {isNone && <span>{stateHeight}</span>}
      {isDashed && (
        <StyledDashedLine
          className={removeDot(CSS_CLASSES.DIVIDER_LINE)}
          $color={$spacingDividerColor}
          stylesheet={stylesheet}
        />
      )}
      {isSolid && (
        <StyledSolidLine
          className={removeDot(CSS_CLASSES.DIVIDER_LINE)}
          $color={$spacingDividerColor}
          stylesheet={stylesheet}
        />
      )}

      <BorderCircle />
    </StyledSpacerWidget>
  ) : (
    <StyledReadOnlySpacerWidget height={height}>
      {isDashed && (
        <StyledDashedLine
          className={removeDot(CSS_CLASSES.DIVIDER_LINE)}
          $color={$spacingDividerColor}
          stylesheet={stylesheet}
        />
      )}
      {isSolid && (
        <StyledSolidLine
          className={removeDot(CSS_CLASSES.DIVIDER_LINE)}
          $color={$spacingDividerColor}
          stylesheet={stylesheet}
        />
      )}
    </StyledReadOnlySpacerWidget>
  );
};

export default SpacerWidget;

const BorderCircle = styled.div`
  display: none;
  position: absolute;
  height: 10px;
  width: 10px;
  background-color: ${token.gray800};
  bottom: -6px;
  border-radius: ${token.radiusCircle};
`;

const StyledSpacerWidget = styled(Rnd)`
  display: flex !important;
  justify-content: center;
  align-items: center;
  position: relative !important;
  width: 100%;
  border-radius: ${token.radiusMedium};
  border: 1px dashed ${token.gray300};
  height: ${({ height }) => height}px;
  &:hover {
    border: 2px solid ${token.gray800};
    & span {
      display: block;
    }
    & ${BorderCircle} {
      display: block;
    }
  }
  & span {
    display: none;
    color: ${token.gray500};
  }
`;

interface ReadOnlySpacerProps {
  height: number;
}

const StyledReadOnlySpacerWidget = styled.div<ReadOnlySpacerProps>`
  width: 100%;
  height: ${({ height }) => height}px;
  display: flex;
  align-items: center;
`;

const StyledDashedLine = styled.div<{ $color?: string; stylesheet?: string }>`
  width: 100%;
  border: 1px dashed ${({ $color }) => ($color ? $color : token.gray300a)};
  ${props => props.stylesheet};
`;

const StyledSolidLine = styled.div<{ $color?: string; stylesheet?: string }>`
  width: 100%;
  border: 1px solid ${({ $color }) => ($color ? $color : token.gray300a)};
  ${props => props.stylesheet};
`;
